import React, {useEffect, useState, useCallback} from "react";
import {MapContainer, TileLayer, LayersControl, GeoJSON} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {GeoJsonObject, Feature} from "geojson";
import {LatLngExpression} from "leaflet";
import "./RosRiverMapComponent.css";
import L from "leaflet";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import {eventService} from "../../services/Events.service";
import {Subscription} from "rxjs";
import hydroTechIcon from '../../hydro-power.png';
import droppIcon from '../../dropp.png';
import {LAYER_FILES_URL, LAYER_NAMES} from "../../shared/enums";

const monitoringApiBaseUrl = 'https://api.monitoring.davr.gov.ua/monitoringGEOJSONV2';
const postIds = [
  38554, // UA_M5.1.2_0406_01
  27022, // UA_M5.1.2_0417_01
  38314, // UA_M5.1.2_0421_01
  27023, // UA_M5.1.2_0423_01 - check this one, expected UA_M5.1.2_0424_01
  27568, // UA_M5.1.2_0425_02
  27027, // UA_M5.1.2_0428_01
  38016, // UA_M5.1.2_0483_01
  38028, // UA_M5.1.2_0552_01
  38032, // UA_M5.1.2_0705_01
];


// Fix missing marker icons in Leaflet
const DefaultIcon = L.icon({
  iconUrl: hydroTechIcon,
  shadowUrl: markerShadow,
  iconSize: [30, 30],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const DroppLeafletIcon = L.icon({
  iconUrl: droppIcon,
  shadowUrl: markerShadow,
  iconSize: [30, 30],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

L.Marker.prototype.options.icon = DefaultIcon;

interface GeojsonLayer {
  name: string;
  data: GeoJsonObject;
}

interface RosRiverMapProps {
  onEachFeature: (feature: Feature, layerName: string) => void;
}

const geojsonFiles = Object.values(LAYER_FILES_URL);

const RosRiverMapComponent: React.FC<RosRiverMapProps> = ({onEachFeature}) => {
  const [geojsonLayers, setGeojsonLayers] = useState<GeojsonLayer[]>([]);
  const [selectedLayer, setSelectedLayer] = useState<GeojsonLayer | null>(null);

  const memoizedOnEachFeature = useCallback(
    (layerName: string) => (feature: Feature, layer: L.Layer) => {
      if (!feature.properties) return;

      layer.on("click", () => {
        onEachFeature(feature, layerName);
      });
    },
    [onEachFeature]
  );

  const fetchGeojsonData = useCallback(async () => {
    try {
      const layers = await Promise.all(
        geojsonFiles.map(async (file) => {
          const response = await fetch(file);
          if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
          const data = await response.json();
          return {name: LAYER_NAMES[file], data};
        })
      );
      const monitoringLayer = await fetchMonitoring();
      layers.push(monitoringLayer);
      setGeojsonLayers(layers);
      setSelectedLayer(layers[0] || null);
    } catch (error) {
      console.error("Error loading GeoJSON:", error);
    }
  }, []);

  const fetchMonitoring = async (): Promise<{name: string, data: any;}> => {
    const monitoringUrl = `${monitoringApiBaseUrl}?post_id_DB=${postIds.join(',')}`;
    const monitoringResponse = await fetch(monitoringUrl);
    if (!monitoringResponse.ok) throw new Error(`HTTP error! Status: ${monitoringResponse.status}`);
    const data = await monitoringResponse.json();
    return {name: "Моніторинг", data};
  };

  useEffect(() => {
    fetchGeojsonData();
  }, [fetchGeojsonData]);

  useEffect(() => {
    const sub: Subscription = eventService.getClickStream().subscribe((option: string) => {
      const selected = geojsonLayers.find((layer) => layer.name === option);
      setSelectedLayer(selected ?? null);
    });

    return () => sub.unsubscribe();
  }, [geojsonLayers]);

  const mapCenter: LatLngExpression = [49.65, 30.75];

  return (
    <div className="map-container">
      <MapContainer center={mapCenter} zoom={8} className="map-view">
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
        <LayersControl position="topright">
          {geojsonLayers.length > 0 ? (
            geojsonLayers.map((layer, index) => (
              <LayersControl.Overlay
                key={index}
                name={layer.name}
                checked={selectedLayer?.name === layer.name}
              >
                <GeoJSON
                  data={layer.data}
                  onEachFeature={(feature, Llayer) =>
                    memoizedOnEachFeature(layer.name)(feature, Llayer)
                  }
                  pointToLayer={
                    layer.name === "Моніторинг"
                      ? (feature, latlng) => L.marker(latlng, {icon: DroppLeafletIcon})
                      : undefined
                  }
                />
              </LayersControl.Overlay>
            ))
          ) : (<div className="loading-message">Завантаження картографічних даних...</div>
          )}
        </LayersControl>
      </MapContainer>
    </div>
  );
};

export default RosRiverMapComponent;
